import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { type RouteComponentProps, withRouter } from "react-router-dom";
import { Download, Warehouse } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import {
  addAggregator,
  addAggregatorReset,
  aggregatorDetails,
  aggregatorDetailsReset,
  updateAggregator,
  updateAggregatorReset,
  listAggregatorAdministrators,
  listAggregatorAdministratorsReset,
  addAggregatorAdministrator,
  addAggregatorAdministratorReset,
  updateAggregatorAdministrator,
  updateAggregatorAdministratorReset,
  deleteAggregatorAdministrator,
  deleteAggregatorAdministratorReset,
  aggregatorAdministratorDetails,
  aggregatorAdministratorDetailsReset,
  listLanguages,
  treeAdministrativeAreas,
  listOrgTypes,
} from "../../actions";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import Form from "./components/Form";
import {
  getStatusErrorMessage,
  valueOrDefault,
  isEmpty,
  completeUrl,
  getCountryObjectFromCache,
  getValidationErrorMessage,
} from "../../utils";
import {
  adaptOption,
  adaptAggregatorAdministrators,
  adaptGenders,
  adaptCardTypes,
  adaptOrganizationTypes,
  adaptLanguages,
  adaptAdministrativeAreas,
  adaptPhoneNumber,
  removeCountryCode,
  adaptLanguageForInitialValues,
  adaptLocationForInitialValues,
} from "../../utils/adapters";
import { CARD_TYPES, GENDERS, MCF_COOPERATING_PARTNER_ORG_TYPE } from "../../constants";
import { WFPButton } from "../../components/ui/button";
import { useTranslation } from "react-i18next";
import type { AggregatorDetailsProfileFormValues } from "./components/Form/Components/AggregatorDetailsProfile";
import type { AdministratorFormValues } from "./components/Form/Components/AdministratorModal";
import type {
  AggregatorAdministrator,
  EntityId,
  OptionValue,
  SelectOption,
} from "./components/Form/Components/types";
import type {
  AdministratorData,
  AdministratorSavePayload,
  AggregatorDetailsData,
  AggregatorDetailsProps,
  AggregatorLocationState,
  AggregatorProfileSubmitValues,
  AggregatorSavePayload,
  AppDispatch,
  DispatchProps,
  RootState,
  StateProps,
} from "./types";

// Casts a thunk/action dispatch (from the untyped generated actions) to the
// return type the caller relies on. Mirrors src/containers/Aggregators/index.tsx.
const dispatchAction = <T = void,>(dispatch: AppDispatch, action: unknown): T =>
  dispatch(action as Parameters<AppDispatch>[0]) as unknown as T;

// Typed wrappers around the untyped `valueOrDefault` util, mirroring
// src/containers/TraderDetails/index.tsx.
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const asRecord = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});

const getOptionalString = (value: unknown): string | undefined =>
  valueOrDefault(value, undefined) as string | undefined;

const getOptionalYear = (value: unknown): string | number | undefined =>
  valueOrDefault(value, undefined) as string | number | undefined;

const getOptionValue = (value: unknown): OptionValue | undefined => {
  if (isRecord(value) && "value" in value) {
    return valueOrDefault(value.value, undefined) as OptionValue | undefined;
  }
  return valueOrDefault(value, undefined) as OptionValue | undefined;
};

// A function to adapt values entered in form to a format accepted by the server
export const adaptFromValues = (
  formValues: AggregatorProfileSubmitValues
): AggregatorSavePayload => ({
  name: getOptionalString(formValues.name),
  other_info: getOptionalString(formValues.other_info),
  contact_email: getOptionalString(formValues.contact_email),
  managed_individuals:
    formValues.org_type && formValues.org_type.label === MCF_COOPERATING_PARTNER_ORG_TYPE
      ? ["mastercard_foundation_participants"]
      : ["farmers"],
  phone: adaptPhoneNumber(formValues.phone) as string | undefined,
  legal_address: getOptionalString(formValues.legal_address),
  physical_address: getOptionalString(formValues.physical_address),
  district: getOptionalString(formValues.district),
  location: getOptionValue(formValues.location),
  org_type: getOptionValue(formValues.org_type),
  language: getOptionValue(formValues.language),
  is_wfp_vendor: typeof formValues.is_wfp_vendor === "boolean" ? formValues.is_wfp_vendor : false,
});

// A function to adapt values entered in form to a format accepted by the server
export const adaptAdministratorFromValues = (
  formValues: Partial<AdministratorFormValues>,
  aggregator: EntityId
): AdministratorSavePayload => ({
  person: {
    uuid: getOptionalString(formValues.uuid) ?? uuidv4(),
    first_name: getOptionalString(formValues.first_name),
    middle_name: getOptionalString(formValues.middle_name),
    last_name: getOptionalString(formValues.last_name),
    contact_phone: adaptPhoneNumber(formValues.contact_phone) as string | undefined,
    identification_card_number: getOptionalString(formValues.identification_card_number),
    gender: getOptionValue(formValues.gender),
    year_of_birth: getOptionalYear(formValues.year_of_birth),
  },
  account_type: getOptionalString(formValues.account_type),
  email: getOptionalString(formValues.email),
  role: getOptionalString(formValues.role),
  aggregator,
});

// A function to adapt values received from server to
// a format compatible with the form
export const adaptInitialFromValues = (
  data: AggregatorDetailsData,
  orgTypes: SelectOption[],
  administrativeAreas: unknown
): AggregatorDetailsProfileFormValues => {
  if (isEmpty(data)) {
    return {};
  }

  const detailsData: Record<string, unknown> = { ...data };
  [
    "legal_registration_status",
    "year_of_establishment",
    "tier",
    "wings_vendor_id",
    "legally_recognized",
    "membership_fee",
    "membership_fee_needed",
    "has_membership_structure",
    "umbrella_organization",
  ].forEach(field => {
    delete detailsData[field];
  });

  return {
    ...detailsData,
    org_type: adaptOption(
      data.org_type && orgTypes.find(status => status.value === data.org_type),
      "value",
      "label"
    ),
    language:
      typeof data.language === "string" ? adaptLanguageForInitialValues(data.language) : undefined,
    phone: data.phone ? removeCountryCode(data.phone) : undefined,
    location: adaptLocationForInitialValues(data, administrativeAreas),
  } as AggregatorDetailsProfileFormValues;
};

// A function to adapt values received from server to
// a format compatible with the form
export const adaptAdministratorInitialFromValues = (
  data: AdministratorData
): Partial<AdministratorFormValues> => {
  const person = data?.person;
  if (!person) {
    return {};
  }

  const genderOption = person.gender
    ? adaptCardTypes(GENDERS).find((gender: SelectOption) => person.gender === gender.value)
    : undefined;

  return {
    uuid: getOptionalString(person.uuid) ?? uuidv4(),
    first_name: getOptionalString(person.first_name),
    middle_name: getOptionalString(person.middle_name),
    last_name: getOptionalString(person.last_name),
    contact_phone: getOptionalString(removeCountryCode(person.contact_phone)),
    location: valueOrDefault(person.location, undefined),
    role: getOptionalString(data.role),
    identification_card_number: getOptionalString(person.identification_card_number),
    gender: valueOrDefault(genderOption, undefined),
    year_of_birth: getOptionalYear(person.year_of_birth),
    email: getOptionalString(data.email),
  };
};

// A function to adapt field errors for Administrator
// record to a flat object
export const adaptAdministratorFieldErrors = (error: unknown): Record<string, unknown> => {
  if (isEmpty(error)) {
    return {};
  }

  const errorRecord = asRecord(error);
  if (Object.hasOwnProperty.call(errorRecord, "data")) {
    const data = asRecord(errorRecord.data);
    return { ...asRecord(data.person), ...data };
  }

  return { ...asRecord(errorRecord.person), ...errorRecord };
};

// This component is used to display the detail
// of a Aggregator Organization
export const AggregatorDetails = ({
  isFetching,
  errorMessage,
  addAggregator,
  addAggregatorReset,
  updateAggregator,
  updateAggregatorReset,
  isSavingProfile,
  history,
  id,
  aggregatorDetails,
  aggregatorDetailsReset,
  initialFormValues,
  listAggregatorAdministrators,
  listAggregatorAdministratorsReset,
  addAggregatorAdministrator,
  addAggregatorAdministratorReset,
  updateAggregatorAdministrator,
  updateAggregatorAdministratorReset,
  deleteAggregatorAdministrator,
  aggregatorAdministrators,
  isFetchingAggregatorAdministrators,
  isFetchingAdministrator,
  errorMessageAdministrator,
  aggregatorAdministratorDetails,
  aggregatorAdministratorDetailsReset,
  cardTypes,
  initialFormValuesAdministrator,
  isOnEditAdministrator,
  isFetchingDeleteAdministrator,
  errorMessageDeleteAdministrator,
  organizationTypes,
  listLanguages,
  languages,
  deleteAggregatorAdministratorReset,
  treeAdministrativeAreas,
  administrativeAreas,
  listOrgTypes,
  fieldErrorsAdministrator,
}: AggregatorDetailsProps) => {
  // This state is used to manage the current in edit record
  const [inEditAdministrator, setInEditAdministrator] = useState<EntityId>(undefined);

  // A state to decide if the delete modal is open or closed
  const [isDeleteAdministratorModalOpen, setIsDeleteAdministratorModalOpen] = useState(false);

  // A state to store the current record to delete
  const [currentToDeleteAdministrator, setCurrentToDeleteAdministrator] =
    useState<AggregatorAdministrator>();

  // A state to decide if the enable modal is open or closed
  const [isEnableAdministratorModalOpen, setIsEnableAdministratorModalOpen] = useState(false);

  // A state to store the current record to enable
  const [currentToEnableAdministrator, setCurrentToEnableAdministrator] =
    useState<AggregatorAdministrator>();

  const { t } = useTranslation();

  useEffect(() => {
    if (id) {
      aggregatorDetails(id);
      listAggregatorAdministrators(id);
    }
  }, [aggregatorDetails, id, listAggregatorAdministrators]);

  // On component mount the supporting data are fetched.
  // On component unmount data are reset.
  useEffect(() => {
    listLanguages();
    const country = getCountryObjectFromCache();
    if (country) {
      treeAdministrativeAreas(country.id);
      listOrgTypes();
    }

    return () => {
      addAggregatorReset();
      updateAggregatorReset();
      aggregatorDetailsReset();
      listAggregatorAdministratorsReset();
      addAggregatorAdministratorReset();
      updateAggregatorAdministratorReset();
      aggregatorAdministratorDetailsReset();
      deleteAggregatorAdministratorReset();
    };
  }, [
    addAggregatorAdministratorReset,
    addAggregatorReset,
    aggregatorAdministratorDetailsReset,
    aggregatorDetailsReset,
    deleteAggregatorAdministratorReset,
    listAggregatorAdministratorsReset,
    listLanguages,
    listOrgTypes,
    treeAdministrativeAreas,
    updateAggregatorAdministratorReset,
    updateAggregatorReset,
  ]);

  // On click validate a requests are made
  // to save Aggregator, after saving the
  // Aggregators list is displayed
  const onClickValidateAndClose = async (formValues: AggregatorDetailsProfileFormValues) => {
    await saveOrganization(true, formValues);
  };

  // This function is used to save a Aggregator Organization
  const saveOrganization = (close: boolean, formValues: AggregatorProfileSubmitValues) => {
    const redirect = (savedId: EntityId) => {
      if (close) {
        history.push("/wfpManager/aggregators");
      } else {
        history.push(`/wfpManager/aggregatorDetails/${savedId}`, {
          fromStep: 1,
        });
      }
    };
    const data = adaptFromValues(formValues);
    let promise;
    if (id) {
      promise = updateAggregator(id, data);
    } else {
      promise = addAggregator(data);
    }
    return promise.then(result => {
      if (result) {
        const savedAggregatorId = result.data.id;
        redirect(savedAggregatorId);
        if (id) {
          aggregatorDetails(savedAggregatorId);
          listAggregatorAdministrators(savedAggregatorId);
        }
      }
      return result;
    });
  };

  // On click open Aggregator Administrator modal the modal is displayed
  const onClickAddAdministrator = () => {
    setInEditAdministrator(undefined);
  };

  const onClickCloseAdministrator = () => {
    setInEditAdministrator(undefined);
    aggregatorAdministratorDetailsReset();
  };

  // On click validate Aggregator Administrator modal the modal is closed
  // if request is successful
  const onClickValidateAdministratorModal = (formValues: AdministratorFormValues) => {
    const reset = () => {
      history.push(`/wfpManager/aggregatorDetails/${id}`, {
        fromStep: 1,
      });
      listAggregatorAdministrators(id);
      setInEditAdministrator(undefined);
    };
    const values = adaptAdministratorFromValues(formValues, id);
    if (inEditAdministrator) {
      updateAggregatorAdministrator(inEditAdministrator, values).then(result => {
        if (result) {
          reset();
        }
      });
    } else {
      addAggregatorAdministrator(values).then(result => {
        if (result) {
          reset();
        }
      });
    }
  };

  // On click save and continue editing
  // the record of Organization is saved
  // and the user is then redirected to edit page
  const onClickValidateAndContinue = async (formValues: AggregatorDetailsProfileFormValues) => {
    await saveOrganization(false, formValues);
  };

  // On click edit on Aggregator Administrator the modal
  // is displayed to allow user modify the record
  const onClickEditAdministrator = (record: AggregatorAdministrator) => {
    aggregatorAdministratorDetails(record.id).then(result => {
      if (result) {
        updateAggregatorAdministratorReset();
        addAggregatorAdministratorReset();
        setInEditAdministrator(record.id);
      }
    });
  };

  // When user clicks edit Aggregator, it is redirected
  // to Aggregator edit page
  const onClickDeleteAdministrator = (record: AggregatorAdministrator) => {
    setIsDeleteAdministratorModalOpen(true);
    setCurrentToDeleteAdministrator(record);
  };

  const onClickEnableAdministrator = (record: AggregatorAdministrator) => {
    setIsEnableAdministratorModalOpen(true);
    setCurrentToEnableAdministrator(record);
  };

  // When user confirm that it want to delete the record
  // the record is actually deleted
  const onClickConfirmDeleteAdministrator = () => {
    if (!currentToDeleteAdministrator) {
      return;
    }
    deleteAggregatorAdministrator(currentToDeleteAdministrator.id).then(result => {
      if (result) {
        setIsDeleteAdministratorModalOpen(false);
        setCurrentToDeleteAdministrator(undefined);
        listAggregatorAdministrators(id);
        deleteAggregatorAdministratorReset();
      }
    });
  };

  // When user press cancel on delete modal
  // the modal is closed and the current record
  // to delete is reset
  const onClickCancelDeleteAdministrator = () => {
    setIsDeleteAdministratorModalOpen(false);
    setCurrentToDeleteAdministrator(undefined);
    deleteAggregatorAdministratorReset();
  };

  // When user confirms that they want to enable the record
  // the record is updated with active: true
  const onClickConfirmEnableAdministrator = () => {
    if (!currentToEnableAdministrator) {
      return;
    }
    const enableId = currentToEnableAdministrator.id;
    // First get the administrator details to have all necessary data
    aggregatorAdministratorDetails(enableId).then(result => {
      if (result) {
        // Convert server data to form format, then set active to true
        const formValues = adaptAdministratorInitialFromValues(result.data);
        const enabledFormValues = {
          ...formValues,
          active: true,
        };

        // Use the adapter to format data for server (with aggregator ID only)
        const enabledAdministratorData = adaptAdministratorFromValues(enabledFormValues, id);

        // Add the active field to the payload
        const finalPayload = {
          ...enabledAdministratorData,
          active: true,
        };

        // Update the administrator
        updateAggregatorAdministrator(enableId, finalPayload).then(updateResult => {
          if (updateResult) {
            // Reset states and refresh the list (similar to onClickEditAdministrator)
            setIsEnableAdministratorModalOpen(false);
            setCurrentToEnableAdministrator(undefined);
            updateAggregatorAdministratorReset();
            aggregatorAdministratorDetailsReset();
            listAggregatorAdministrators(id);
          }
        });
      }
    });
  };

  // When user press cancel on enable modal
  // the modal is closed and the current record
  // to enable is reset
  const onClickCancelEnableAdministrator = () => {
    setIsEnableAdministratorModalOpen(false);
    setCurrentToEnableAdministrator(undefined);
    updateAggregatorAdministratorReset();
    aggregatorAdministratorDetailsReset();
  };

  // On click download template the link is open to download the file
  const onClickDownloadTemplate = () => {
    const country = getCountryObjectFromCache();
    const link = `${completeUrl(process.env.API_BASE_URL ?? "")}country/${
      country.code
    }/api/farmer-import-template/`;
    window.location.assign(link);
  };

  const startFromStep = (history.location?.state as AggregatorLocationState | undefined)?.fromStep;

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("aggregators.breadcrumb"), href: "/wfpManager/aggregators" },
            { label: t("aggregatorDetails.pageTitle") },
          ]}
        />
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-x-2">
            <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 text-ivory-600 bg-ivory-100">
              <Warehouse />
            </span>
            <h1 className="font-semibold text-2xl">{t("aggregatorDetails.pageTitle")}</h1>
          </div>
          <WFPButton type="button" onClick={onClickDownloadTemplate}>
            <Download className="size-4" />
            {t("aggregatorDetails.downloadFarmersTemplate")}
          </WFPButton>
        </div>
      </div>
      <div className="w-full">
        <Form
          id={id}
          initialValues={initialFormValues}
          isFetching={isFetching}
          isSavingProfile={isSavingProfile}
          isFetchingAggregatorAdministrators={isFetchingAggregatorAdministrators}
          errorMessage={errorMessage}
          onClickValidateAndClose={onClickValidateAndClose}
          onClickValidateAndContinue={onClickValidateAndContinue}
          onClickAddAdministrator={onClickAddAdministrator}
          aggregatorAdministrators={aggregatorAdministrators}
          onClickEditAdministrator={onClickEditAdministrator}
          onClickDeleteAdministrator={onClickDeleteAdministrator}
          onClickEnableAdministrator={onClickEnableAdministrator}
          isDeleteAdministratorModalOpen={isDeleteAdministratorModalOpen}
          onClickConfirmDeleteAdministrator={onClickConfirmDeleteAdministrator}
          onClickCancelDeleteAdministrator={onClickCancelDeleteAdministrator}
          currentToDeleteAdministrator={currentToDeleteAdministrator}
          isFetchingDeleteAdministrator={isFetchingDeleteAdministrator}
          errorMessageDeleteAdministrator={errorMessageDeleteAdministrator}
          isEnableAdministratorModalOpen={isEnableAdministratorModalOpen}
          onClickConfirmEnableAdministrator={onClickConfirmEnableAdministrator}
          onClickCancelEnableAdministrator={onClickCancelEnableAdministrator}
          currentToEnableAdministrator={currentToEnableAdministrator}
          isFetchingEnableAdministrator={isFetchingAdministrator}
          errorMessageEnableAdministrator={errorMessageAdministrator}
          organizationTypes={organizationTypes}
          languages={languages}
          administrativeAreas={administrativeAreas}
          initialStep={startFromStep}
          setFieldError={() => {}}
          initialFormValuesAdministrator={initialFormValuesAdministrator}
          isFetchingAdministrator={isFetchingAdministrator}
          errorMessageAdministrator={errorMessageAdministrator}
          fieldErrorsAdministrator={fieldErrorsAdministrator}
          onClickValidateAdministratorModal={onClickValidateAdministratorModal}
          cardTypes={cardTypes}
          isOnEditAdministrator={isOnEditAdministrator}
          onClickCloseAdministrator={() => onClickCloseAdministrator()}
        />
      </div>
    </main>
  );
};

// Starting from the redux state it gets data related to logged in user
export const mapStateToProps = (
  state: RootState,
  props: RouteComponentProps<{ id: string }>
): StateProps => {
  const id = props.match.params.id ?? "";
  const hasAggregatorDetails = !isEmpty(state.aggregatorDetails.data);
  const hasJustSavedAggregator =
    !isEmpty(state.addAggregator.data) || !isEmpty(state.updateAggregator.data);
  const isFetchingInitialDetails =
    !!id && state.aggregatorDetails.isFetching && !hasAggregatorDetails && !hasJustSavedAggregator;

  return {
    // Current on edit aggregator
    id,
    initialFormValues: adaptInitialFromValues(
      state.aggregatorDetails.data,
      adaptOrganizationTypes(state.listOrgTypes.data.results),
      adaptAdministrativeAreas(state.treeAdministrativeAreas.data.administrative_areas)
    ),
    // Loading and errors properties
    isFetching:
      isFetchingInitialDetails ||
      state.listLanguages.isFetching ||
      state.listOrgTypes.isFetching ||
      state.treeAdministrativeAreas.isFetching ||
      false,
    isSavingProfile: state.addAggregator.isLoading || state.updateAggregator.isLoading || false,
    isFetchingAggregatorAdministrators: state.listAggregatorAdministrators.isFetching || false,
    errorMessage:
      state.listAggregatorAdministrators.errorMessage ||
      state.aggregatorDetails.errorMessage ||
      state.listLanguages.errorMessage ||
      state.treeAdministrativeAreas.errorMessage ||
      state.listOrgTypes.errorMessage ||
      getStatusErrorMessage(state.addAggregator.error) ||
      getStatusErrorMessage(state.updateAggregator.error) ||
      "",
    // Related entities lists
    cardTypes: adaptCardTypes(CARD_TYPES),
    genders: adaptGenders(GENDERS),
    organizationTypes: adaptOrganizationTypes(state.listOrgTypes.data.results),
    languages: adaptLanguages(state.listLanguages.data.results),
    administrativeAreas: adaptAdministrativeAreas(
      state.treeAdministrativeAreas.data.administrative_areas
    ),
    // Aggregator Administrators list properties
    aggregatorAdministrators: adaptAggregatorAdministrators(
      state.listAggregatorAdministrators.data.results
    ),
    // Properties related to Administrator modal
    isFetchingAdministrator:
      state.addAggregatorAdministrator.isLoading ||
      state.updateAggregatorAdministrator.isLoading ||
      state.aggregatorAdministratorDetails.isFetching ||
      false,
    errorMessageAdministrator:
      getStatusErrorMessage(state.addAggregatorAdministrator.error) ||
      getValidationErrorMessage(state.addAggregatorAdministrator.error) ||
      getStatusErrorMessage(state.updateAggregatorAdministrator.error) ||
      "",
    // Form errors in Aggregator Administrator form
    fieldErrorsAdministrator:
      adaptAdministratorFieldErrors(state.addAggregatorAdministrator.error) ||
      adaptAdministratorFieldErrors(state.updateAggregatorAdministrator.error) ||
      {},
    // Current on edit Aggregator Administrator
    initialFormValuesAdministrator: adaptAdministratorInitialFromValues(
      state.aggregatorAdministratorDetails.data
    ),
    // Current on edit Administrator
    isOnEditAdministrator: state.aggregatorAdministratorDetails.data.id !== undefined,
    // Delete Administrator properties
    isFetchingDeleteAdministrator: state.deleteAggregatorAdministrator.isLoading || false,
    errorMessageDeleteAdministrator: state.deleteAggregatorAdministrator.errorMessage || "",
  };
};

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    addAggregator: data => dispatchAction(dispatch, addAggregator(data)),
    addAggregatorReset: () => dispatchAction(dispatch, addAggregatorReset()),
    updateAggregator: (id, data) => dispatchAction(dispatch, updateAggregator(id, data)),
    updateAggregatorReset: () => dispatchAction(dispatch, updateAggregatorReset()),
    aggregatorDetails: id => dispatchAction(dispatch, aggregatorDetails(id as string)),
    aggregatorDetailsReset: () => dispatchAction(dispatch, aggregatorDetailsReset()),
    listAggregatorAdministrators: aggregator =>
      dispatchAction(dispatch, listAggregatorAdministrators({ aggregator })),
    listAggregatorAdministratorsReset: () =>
      dispatchAction(dispatch, listAggregatorAdministratorsReset()),
    addAggregatorAdministrator: data => dispatchAction(dispatch, addAggregatorAdministrator(data)),
    addAggregatorAdministratorReset: () =>
      dispatchAction(dispatch, addAggregatorAdministratorReset()),
    updateAggregatorAdministrator: (id, data) =>
      dispatchAction(dispatch, updateAggregatorAdministrator(id, data)),
    updateAggregatorAdministratorReset: () =>
      dispatchAction(dispatch, updateAggregatorAdministratorReset()),
    deleteAggregatorAdministrator: id =>
      dispatchAction(dispatch, deleteAggregatorAdministrator(id)),
    aggregatorAdministratorDetails: id =>
      dispatchAction(dispatch, aggregatorAdministratorDetails(id as string)),
    aggregatorAdministratorDetailsReset: () =>
      dispatchAction(dispatch, aggregatorAdministratorDetailsReset()),
    listLanguages: () => dispatchAction(dispatch, listLanguages()),
    deleteAggregatorAdministratorReset: () =>
      dispatchAction(dispatch, deleteAggregatorAdministratorReset()),
    treeAdministrativeAreas: countryId =>
      dispatchAction(dispatch, treeAdministrativeAreas({}, `${countryId}/administrative_areas/`)),
    listOrgTypes: () => dispatchAction(dispatch, listOrgTypes()),
  };
};

// The component uses the redux store
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AggregatorDetails));
